import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'

import {
  useGetAuthenticatedProductsListInfiniteQuery,
  useGetPublicProductsListInfiniteQuery
} from '@cannect/services/resources/products'

import { useProductClassId } from '@cannect/services/resources/classes'
import { ClassId } from '@cannect/constants/productFilters'
import { SEARCH_DEBOUNCE_MS } from '@cannect/constants/products'
import useDebounce from '@cannect/hooks/utils/useDebounce'
import { useQueryFilters } from '@cannect/hooks/utils/useQueryFilters'
import { useMarketPlace } from 'hooks/useMarketPlace'
import useMediaQuery from 'hooks/useMediaQuery'
import { useTokenApi } from 'hooks/useTokenApi'

import { MOBILE_WIDTH } from 'utils/constants'

import {
  BESTSELLER_ORDER_BY,
  BESTSELLER_ORDER_DIR,
  DEFAULT_PAGE_SIZE,
  DISPLAY_OPTIONS,
  FLOWER_SHAPE_ID,
  ORDER_SELECT_OPTIONS,
  PAGE_SIZE_WITHOUT_FILTERS,
  PRICE_ORDER_BY,
  PRICE_ORDER_DIR,
  RELEVANCE_ORDER_BY
} from './constants'
import { TDisplayMode } from './types'

export const useProductsList = (options?: {
  basePath?: string
  requireParamKey?: string
  collapsibleFilters?: boolean
}) => {
  const basePath = options?.basePath ?? '/produtos'
  const hasCollapsibleFilters = options?.collapsibleFilters ?? false
  const { tokenApi } = useTokenApi()
  const { pagesProducts } = useParams<{ pagesProducts: string }>()
  const history = useHistory()
  const location = useLocation()
  const isMobile = useMediaQuery(MOBILE_WIDTH)

  const { queryParams, updateQueryParams } = useQueryFilters<{
    supplier_id: string[]
    brand_id: string[]
    shape_id: string[]
    class_id: string[]
    category_id: string[]
    cbd_concentration: string[]
    thc_concentration: string[]
    cannabidiol_id: string[]
    pathology: string[]
    veiculo: string[]
    extraction_method: string[]
    gmp: string[]
    proportion_id: string[]
    translated_name: string
    order_by: string
    order_dir: string
    layout: string
  }>()

  const { filterOrderList } = useMarketPlace()

  const [search, setSearch] = useState('')
  const debouncedSearchTerm = useDebounce(search, SEARCH_DEBOUNCE_MS)

  const currentPage = useMemo(() => parseInt(pagesProducts, 10) || 1, [pagesProducts])

  const previousFiltersRef = useRef<string>('')
  const isInitialMountRef = useRef(true)
  const isInitialSearchMountRef = useRef(true)

  const filterKeys = useMemo(
    () => [
      'supplier_id',
      'brand_id',
      'shape_id',
      'class_id',
      'category_id',
      'category_slug',
      'cbd_concentration',
      'thc_concentration',
      'cannabidiol_id',
      'pathology',
      'veiculo',
      'extraction_method',
      'gmp',
      'curatorship_range',
      'terpene_id',
      'min_price',
      'max_price',
      'delivery_by_association',
      'cannabinoid_name',
      'volume_name',
      'proportion_id'
    ],
    []
  )

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      const initialFilters = filterKeys
        .map((key) => {
          const values = queryParams.getAll(key)
          return values.length > 0 ? `${key}:${values.sort().join(',')}` : null
        })
        .filter(Boolean)
        .sort()
        .join('|')
      previousFiltersRef.current = initialFilters
      return
    }

    const currentFilters = filterKeys
      .map((key) => {
        const values = queryParams.getAll(key)
        return values.length > 0 ? `${key}:${values.sort().join(',')}` : null
      })
      .filter(Boolean)
      .sort()
      .join('|')

    const previousFilters = previousFiltersRef.current

    if (currentFilters !== previousFilters && currentPage !== 1) {
      const searchParams = new URLSearchParams()
      queryParams.forEach((value, key) => {
        searchParams.append(key, value)
      })
      const searchString = searchParams.toString()
      history.push(`${basePath}/1${searchString ? `?${searchString}` : ''}`)
      previousFiltersRef.current = currentFilters
      return
    }

    previousFiltersRef.current = currentFilters
  }, [queryParams, currentPage, basePath, history, location.search, filterKeys])

  const { getClassIdByName } = useProductClassId()
  // Classes criadas pela sincronizacao do catalogo tem id proprio em cada
  // ambiente; resolvemos pelo nome -- ver useProductClassId.
  const allopathicId = getClassIdByName('Alopático')
  const hygieneBeautyId = getClassIdByName('Higiene e Beleza')

  const isCannabisMedicinalCategory = useMemo(() => {
    const nonCannabisClassIds = new Set(
      [
        ClassId.SUPPLEMENT,
        ClassId.MUSHROOM,
        ClassId.PHARMACY_OTHERS,
        allopathicId,
        hygieneBeautyId,
        ClassId.GENETIC_TEST
      ].filter((id): id is number => id !== undefined)
    )
    return !queryParams.getAll('class_id').some((id) => nonCannabisClassIds.has(Number(id)))
  }, [queryParams, allopathicId, hygieneBeautyId])

  /**
   * Categorias da farmácia Pill, vindas da seção "Tudo em um só lugar" da home e dos círculos de
   * subcategoria. Elas já chegam com o recorte pronto pela collection, então o filtro lateral não
   * tem o que refinar — o painel sai e a listagem ocupa a largura toda.
   */
  const isPillCollectionCategory = useMemo(() => queryParams.has('shopify_collection'), [queryParams])

  /**
   * Cannabis medicinal: é ela que recebe o filtro lateral e o banner promocional. As outras
   * categorias da listagem (suplementos, medicamento, higiene, teste genético) já caem fora por
   * `isCannabisMedicinalCategory`, e as da farmácia Pill pelo `shopify_collection`.
   *
   * A categoria aparece com dois recortes conforme o `CategoryCards` ativo (ver
   * `VITE_APP_GENETIC_TEST_ENABLED`): `?limit=16&translated_name=` no componente novo e
   * `?limit=16&supplier_id=1&translated_name=` no legado. Os dois entram aqui.
   */
  const isCannabisListing = isCannabisMedicinalCategory && !isPillCollectionCategory

  const [isFiltersVisible, setIsFiltersVisible] = useState(isCannabisListing)

  useEffect(() => {
    setIsFiltersVisible(isCannabisListing)
  }, [isCannabisListing])


  const allowsBestsellerOrder = useMemo(() => {
    if (isPillCollectionCategory) return true

    const classIds = queryParams.getAll('class_id')
    return [allopathicId, hygieneBeautyId].some((id) => id !== undefined && classIds.includes(String(id)))
  }, [queryParams, allopathicId, hygieneBeautyId, isPillCollectionCategory])


  const allowedOrderBys = useMemo(() => {
    const allowed = new Set([PRICE_ORDER_BY])
    if (allowsBestsellerOrder) allowed.add(BESTSELLER_ORDER_BY)
    if (isCannabisListing) allowed.add(RELEVANCE_ORDER_BY)
    return allowed
  }, [allowsBestsellerOrder, isCannabisListing])


  const defaultSort = useMemo(() => {
    if (allowsBestsellerOrder) return { orderBy: BESTSELLER_ORDER_BY, orderDir: BESTSELLER_ORDER_DIR }
    if (isCannabisListing) return { orderBy: RELEVANCE_ORDER_BY, orderDir: null }
    return { orderBy: PRICE_ORDER_BY, orderDir: PRICE_ORDER_DIR }
  }, [allowsBestsellerOrder, isCannabisListing])

  const cleanQueryParams = useMemo(() => {
    const params = new URLSearchParams()

    queryParams.forEach((value, key) => {
      params.append(key, value)
    })

    if (debouncedSearchTerm.trim()) {
      params.set('translated_name', debouncedSearchTerm.trim())
    }

    if (filterOrderList.type) {
      params.set('order_by', filterOrderList.type)
    }

    if (!allowedOrderBys.has(params.get('order_by') ?? '')) {
      params.set('order_by', defaultSort.orderBy)

      if (defaultSort.orderDir) {
        params.set('order_dir', defaultSort.orderDir)
      } else {
        params.delete('order_dir')
      }
    }

    if (hasCollapsibleFilters && !isFiltersVisible) {
      params.set('limit', PAGE_SIZE_WITHOUT_FILTERS.toString())
    } else if (!params.has('limit')) {
      params.set('limit', DEFAULT_PAGE_SIZE.toString())
    }

    params.set('page', currentPage.toString())

    return params
  }, [
    queryParams,
    debouncedSearchTerm,
    filterOrderList.type,
    currentPage,
    isFiltersVisible,
    hasCollapsibleFilters,
    allowedOrderBys,
    defaultSort
  ])

  const isRequiredParamPresent = useMemo(() => {
    if (!options?.requireParamKey) return true
    const key = options.requireParamKey
    return cleanQueryParams.has(key) && !!cleanQueryParams.get(key)
  }, [cleanQueryParams, options?.requireParamKey])

  const publicProductsQuery = useGetPublicProductsListInfiniteQuery(
    cleanQueryParams,
    !tokenApi && isRequiredParamPresent
  )

  const authenticatedProductsQuery = useGetAuthenticatedProductsListInfiniteQuery(
    cleanQueryParams,
    !!tokenApi && isRequiredParamPresent
  )

  const activeProductsQuery = tokenApi ? authenticatedProductsQuery : publicProductsQuery

  const { products, filters, totalCount, totalPages, hasNextPage, hasPreviousPage, isFlowerBannerVisible } =
    useMemo(() => {
      const allProducts = activeProductsQuery.data?.pages?.flatMap((page) => page.products.rows) || []
      const filters = activeProductsQuery.data?.pages?.[0]?.products?.filters || {}
      const count = activeProductsQuery.data?.pages?.[0]?.products?.count || 0
      const itemsPerPage = parseInt(cleanQueryParams.get('limit') || DEFAULT_PAGE_SIZE.toString(), 10)
      const pages = Math.ceil(count / itemsPerPage)

      const shapeIds = queryParams.getAll('shape_id')
      const flowerBanner = shapeIds.includes(FLOWER_SHAPE_ID)

      return {
        products: allProducts,
        filters,
        totalCount: count,
        totalPages: pages,
        hasNextPage: currentPage < pages,
        hasPreviousPage: currentPage > 1,
        isFlowerBannerVisible: flowerBanner
      }
    }, [activeProductsQuery.data, cleanQueryParams, queryParams, currentPage])

  useEffect(() => {
    if (isInitialSearchMountRef.current) {
      isInitialSearchMountRef.current = false
      return
    }
    updateQueryParams({ translated_name: debouncedSearchTerm || undefined })
    history.push(`${basePath}/1${location.search}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  const handlePageChange = useCallback(
    (selectedPage: number) => {
      history.push(`${basePath}/${selectedPage}${location.search}`)
    },
    [history, location.search, basePath]
  )

  const handleProductClick = useCallback(
    (productId: number) => {
      history.push(`/produto/${productId}`)
    },
    [history]
  )

  const handleSortChange = useCallback(
    (selectedOption: any) => {
      if (selectedOption) {
        if (selectedOption.value.indexOf('-') === -1) {
          updateQueryParams({ order_by: selectedOption.value })
        } else {
          const [orderBy, orderDir] = selectedOption.value.split('-')

          updateQueryParams({ order_by: orderBy, order_dir: orderDir })
        }
      }
    },
    [updateQueryParams]
  )

  const handleLayoutChange = useCallback(
    (selectedOption: any) => {
      if (selectedOption) {
        updateQueryParams({ layout: selectedOption.value })
      }
    },
    [updateQueryParams]
  )

  const displayMode = useMemo(() => {
    const layoutParam = queryParams.get('layout')
    if (isMobile || !tokenApi) {
      return 'grid' as TDisplayMode
    }
    return (layoutParam || 'grid') as TDisplayMode
  }, [isMobile, tokenApi, queryParams])

  const currentSortOrder = useMemo(() => {
    const orderDir = queryParams.get('order_dir') || null
    const orderBy = queryParams.get('order_by') || ''

    const effectiveOrderBy = orderBy || filterOrderList.type
    const selected = orderDir ? `${effectiveOrderBy}-${orderDir}` : effectiveOrderBy

    if (allowedOrderBys.has(effectiveOrderBy) && ORDER_SELECT_OPTIONS.some((option) => option.value === selected)) {
      return selected
    }

    return defaultSort.orderDir ? `${defaultSort.orderBy}-${defaultSort.orderDir}` : defaultSort.orderBy
  }, [queryParams, filterOrderList.type, allowedOrderBys, defaultSort])

  const sortOptions = useMemo(
    () =>
      ORDER_SELECT_OPTIONS.filter((option) => {
        const [optionOrderBy] = option.value.split('-')
        return allowedOrderBys.has(optionOrderBy)
      }),
    [allowedOrderBys]
  )

  const currentLayout = useMemo(() => {
    return queryParams.get('layout') || 'grid'
  }, [queryParams])

  const isMedicationCategoryActive = useMemo(
    () => allopathicId !== undefined && queryParams.getAll('class_id').includes(String(allopathicId)),
    [queryParams, allopathicId]
  )

  const activeFiltersCount = useMemo(() => {
    let count = 0
    const filterKeys = [
      'supplier_id',
      'brand_id',
      'shape_id',
      'class_id',
      'category_id',
      'cbd_concentration',
      'thc_concentration',
      'cannabidiol_id',
      'pathology',
      'veiculo',
      'extraction_method',
      'gmp',
      'curatorship_range',
      'terpene_id',
      'cannabinoid_name',
      'volume_name',
      'proportion_id',
      'max_price'
    ]
    filterKeys.forEach((key) => {
      count += queryParams.getAll(key).length
    })
    return count
  }, [queryParams])

  return {
    products,
    filters,
    totalCount,
    totalPages,
    currentPage,

    isLoading: activeProductsQuery.isLoading,
    isFetching: activeProductsQuery.isFetching,
    hasNextPage,
    hasPreviousPage,
    error: activeProductsQuery.error,

    displayMode,
    displayOptions: DISPLAY_OPTIONS,
    isFlowerBannerVisible,
    search,
    currentSortOrder,
    currentLayout,
    isMedicationCategoryActive,
    sortOptions,
    isCannabisMedicinalCategory,
    isPillCollectionCategory,
    isCannabisListing,
    isFiltersVisible,
    setIsFiltersVisible,

    isAuthenticated: !!tokenApi,
    isMobile,
    activeFiltersCount,

    handleSearchChange,
    handlePageChange,
    handleProductClick,
    handleSortChange,
    handleLayoutChange,
    fetchNextPage: activeProductsQuery.fetchNextPage
  }
}
